// Single data primitive for the electorate report. Wraps the existing
// fetchArrow/arrowUtils pattern; report bodies call these, never inline fetches.
//
// Keys-based (NZDash has no groupsById registry) — pass groupKeys pulled from
// partyConfig / issueConfig / segmentConfig.
import { fetchArrow, rowsFromTable } from "./arrowUtils";

// Current tracking wave. We pass filters:{ wave } explicitly rather than relying on
// group_by_wave:false, because group_by_wave:false alone blends waves 1+2.
export const CURRENT_WAVE = 2;

function pickLatestWave(rows) {
  if (!rows || !rows.length) return null;
  if (!("wave" in rows[0])) return rows[0];
  return rows.reduce((best, r) => (Number(r.wave) > Number(best.wave) ? r : best), rows[0]);
}

// Fetch a set of metric keys for one electorate + national in parallel, returning
// the report's canonical shape: [{ key, label, constituency, national }].
export async function loadGroupData({
  groupKeys,
  constCode,
  level = "constituency",
  labels = {},
  turnout_weight = "turnout_1_n",
  wave = CURRENT_WAVE,
}) {
  if (!groupKeys || !groupKeys.length) return [];

  const body = (code) => ({
    groupKeys,
    filters: { wave },     // pins to a single wave (no wave blending)
    constCode: code,
    turnout_weight,
    group_by_wave: false,  // one aggregated row per area for the pinned wave
  });

  const [constRows, natRows] = await Promise.all([
    fetchArrow(`/api/${level}_data_arrow/`, body(constCode)).then(rowsFromTable),
    fetchArrow(`/api/national_data_arrow/`, body(null)).then(rowsFromTable),
  ]);

  const c = pickLatestWave(constRows) || {};
  const n = pickLatestWave(natRows) || {};

  return groupKeys.map((k) => ({
    key: k,
    label: labels[k] || k,
    constituency: c[k] ?? null,
    national: n[k] ?? null,
  }));
}

// Fetch ALL rows for a level (one row per area) — used for SA2 choropleths where we
// need every sub-area, not a single aggregated row. Returns raw rows incl. area_id.
export async function loadRows({
  groupKeys,
  constCode,
  level = "SA2",
  turnout_weight = "turnout_1_n",
  wave = CURRENT_WAVE,
}) {
  if (!groupKeys || !groupKeys.length) return [];
  const rows = await fetchArrow(`/api/${level}_data_arrow/`, {
    groupKeys,
    filters: { wave },
    constCode,
    turnout_weight,
    group_by_wave: false,
  }).then(rowsFromTable);
  return rows;
}

// Mockup element #8 — "% of the electorate in play between Labour and X".
// pairs: [[partyA_considerCol, partyB_considerCol], ...] (e.g. ["lab_consider","green_consider"]).
// Returns [{ party_a, party_b, electorate, national, threshold }].
export async function loadOverlap({
  constCode,
  pairs,
  threshold = 6,           // _consider is stored 0-10, so 6 (not 60)
  turnout_weight = "turnout_1_n",
  wave = CURRENT_WAVE,
}) {
  if (!pairs || !pairs.length) return [];
  const table = await fetchArrow(`/api/consideration_overlap/`, {
    constCode,
    pairs,
    threshold,
    turnout_weight,
    filters: { wave },
  });
  return rowsFromTable(table);
}
